import React, { useReducer, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";

import FileUpload from "./FileUpload";
import Chip from "./Chip";
import { uploadArchitectureDiagram } from "../services/threat_model_services";
import { createModelReducer, createModelInitialState } from "../store/reducer";

type Props = {
  open?: boolean;
  onClose?: () => void;
  onStart?: (payload: { title: string; description: string; assumptions: string[] }) => void;
};

// ── Full-screen loader overlay ────────────────────────────────────────────────

const LoaderOverlay: React.FC<{ message: string }> = ({ message: _ }) =>
  ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/70 backdrop-blur-sm">
      <div className="bg-white rounded-2xl flex items-center gap-4 px-10 py-7 shadow-lg">

        {/* Dual-ring spinner */}
        <div className="relative flex items-center justify-center" style={{ width: 48, height: 48 }}>
          {/* Outer purple ring */}
          <svg
            style={{ position: "absolute", width: 48, height: 48, animation: "spin 1s linear infinite" }}
            viewBox="0 0 48 48" fill="none"
          >
            <circle cx="24" cy="24" r="20" stroke="#ede9fe" strokeWidth="4" />
            <circle
              cx="24" cy="24" r="20"
              stroke="#7c3aed"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="94 32"
              transform="rotate(-90 24 24)"
            />
          </svg>

          {/* Inner gray ring */}
          <svg
            style={{ position: "absolute", width: 30, height: 30, animation: "spin 0.7s linear infinite reverse" }}
            viewBox="0 0 30 30" fill="none"
          >
            <circle cx="15" cy="15" r="11" stroke="#e5e7eb" strokeWidth="3.5" />
            <circle
              cx="15" cy="15" r="11"
              stroke="#9ca3af"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeDasharray="42 27"
              transform="rotate(-90 15 15)"
            />
          </svg>
        </div>

        {/* Label */}
        <p className="text-gray-900 font-bold text-base">Processing...</p>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>,
    document.body
  );

// ── Modal ─────────────────────────────────────────────────────────────────────

const CreateModelModal: React.FC<Props> = ({ open = true, onClose, onStart }) => {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(createModelReducer, createModelInitialState);
  const [fieldErrors, setFieldErrors] = useState<{ title?: string; file?: string }>({});
  const {
    title,
    description,
    assumption,
    assumptions,
    selectedFile,
    fileUploaded,
    isLoading,
    loadingMessage,
    error,
  } = state;

  const handleStartModeling = async () => {
    const errors: { title?: string; file?: string } = {};
    if (!title.trim()) errors.title = "Title is required.";
    if (!selectedFile) errors.file = "Please upload an architecture diagram.";
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    dispatch({ type: "SET_ERROR", payload: null });
    try {
      let fileKey = "";

      if (selectedFile) {
        dispatch({ type: "START_LOADING", payload: "Uploading your architecture diagram…" });
        const uploadRes = await uploadArchitectureDiagram(selectedFile, "DEV_API_KEY_TMA", description);
        fileKey = uploadRes?.file_key ?? uploadRes?.url ?? "";
      }

      // Navigate immediately — ModelingProgress will open the stream itself
      navigate("/console/threat-accelerator/processing", {
        state: { fileKey, title, description, assumptions },
      });
    } catch (err: any) {
      dispatch({ type: "SET_ERROR", payload: err?.message ?? "Something went wrong. Please try again." });
      dispatch({ type: "STOP_LOADING" });
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Loader overlay — rendered via portal above everything */}
      {isLoading && <LoaderOverlay message={loadingMessage} />}

      <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
        <div className="w-full max-w-2xl bg-white rounded-2xl p-8 shadow-xl max-h-[80vh] flex flex-col">
            <h3 className="text-purple-600 text-2xl" style={{ fontFamily: "Inter", fontWeight: "bold" }}>Create Model</h3>
         
          <div className="overflow-y-auto flex-1 mt-6 pr-2">
            {/* Title */}
            <div className="mt-0">
              <label className="block text-sm text-gray-800 mb-2" style={{ fontFamily: "Inter", fontWeight: "bold" }}>Title</label>
              <div className="relative flex items-center gap-2">
                <input
                  value={title}
                  onChange={(e) => { dispatch({ type: "SET_TITLE", payload: e.target.value }); setFieldErrors((p) => ({ ...p, title: undefined })); }}
                  className="flex-1 px-4 py-2 rounded-lg border-2 border-blue-200 bg-blue-50 text-gray-900"
                  style={{ fontFamily: "Inter", fontWeight: "regular" }}
                />
                {title.trim() && <div className="text-green-500 text-xl font-bold flex-shrink-0">✓</div>}
              </div>
              {fieldErrors.title && <p className="mt-1 text-red-500 text-xs">{fieldErrors.title}</p>}
            </div>

            {/* File Upload */}
            <div className="mt-6">
              <label className="block text-sm text-gray-800 mb-2" style={{ fontFamily: "Inter", fontWeight: "bold" }}>Upload Architecture Diagram</label>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <FileUpload
                    onFile={(f) => { dispatch({ type: "SET_FILE", payload: f }); setFieldErrors((p) => ({ ...p, file: undefined })); }}
                    onUploadComplete={(done) => dispatch({ type: "SET_FILE_UPLOADED", payload: done })}
                  />
                </div>
                {fileUploaded && <div className="text-green-500 text-xl font-bold flex-shrink-0">✓</div>}
              </div>
              {fieldErrors.file && <p className="mt-1 text-red-500 text-xs">{fieldErrors.file}</p>}
            </div>

            {/* Description */}
            <div className="mt-6">
              <label className="block text-sm text-gray-800 mb-2" style={{ fontFamily: "Inter", fontWeight: "bold" }}>Model Description</label>
              <textarea
                value={description}
                onChange={(e) => dispatch({ type: "SET_DESCRIPTION", payload: e.target.value })}
                placeholder="Enter model description..."
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 text-gray-900 resize-none"
                style={{ fontFamily: "Inter", fontWeight: "regular" }}
              />
            </div>

            {/* Assumptions */}
            <div className="mt-6">
              <label className="block text-sm text-gray-800 mb-2" style={{ fontFamily: "Inter", fontWeight: "bold" }}>Assumption(s)</label>
              <div className="flex gap-5 mb-4">
                <input
                  value={assumption}
                  onChange={(e) => dispatch({ type: "SET_ASSUMPTION_INPUT", payload: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && dispatch({ type: "ADD_ASSUMPTION" })}
                  placeholder="e.g....."
                  className="flex-1 px-4 py-2.5 rounded-lg border-2 border-gray-200 text-gray-900"
                  style={{ fontFamily: "Inter", fontWeight: "regular" }}
                />
                <button
                  onClick={() => dispatch({ type: "ADD_ASSUMPTION" })}
                  className="px-2"
                  style={{ borderRadius: 20, border: "1px solid #8A5CF5", background: "#FFF", color: "#8A5CF5", fontFamily: "Inter", fontSize: 18 }}
                >
                  Enter
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {assumptions.map((a, i) => (
                  <Chip key={i} onRemove={() => dispatch({ type: "REMOVE_ASSUMPTION", payload: i })}>
                    {a}
                  </Chip>
                ))}
              </div>
            </div>
          </div>

          {error && (
            <p className="mt-3 text-red-500 text-xs text-center">{error}</p>
          )}

          <div className="mt-4 flex justify-between gap-4 border-t pt-4">
            <button
              onClick={() => { dispatch({ type: "RESET" }); onClose?.(); }}
              className="border-2 border-purple-500 bg-white text-purple-600 px-8 py-2.5 rounded-full cursor-pointer hover:bg-purple-50 transition text-sm flex-1" style={{ fontFamily: "Inter", fontWeight: "bold" }}
            >
              Previous
            </button>
            <button
              disabled={isLoading}
              onClick={handleStartModeling}
              className="bg-gradient-to-r from-blue-500 to-green-500 text-white border-none px-8 py-2.5 rounded-full cursor-pointer hover:shadow-lg transition text-sm flex-1 disabled:opacity-60 disabled:cursor-not-allowed" style={{ fontFamily: "Inter", fontWeight: "bold" }}
            >
              Start Modeling
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateModelModal;
